import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Select } from '@/components/Select'

// Mirrors the backend PDF_COMPLETION_MODE / PDF_ORIENTATION choices.
export const PDF_COMPLETION_MODES = [
  { id: 'All Pages', label: 'Viewing all pages' },
  { id: 'Last Page', label: 'Viewing the last page' },
  { id: 'Percent of Pages', label: 'Viewing a percentage of pages' },
]

export const PDF_ORIENTATIONS = [
  { id: 'Vertical', label: 'Vertical (scroll through pages)' },
  { id: 'Horizontal', label: 'Horizontal (page-by-page with buttons)' },
]

export const PDF_BLOCK_DEFAULTS = {
  pdfCompletionMode: 'All Pages',
  pdfCompletionPercent: null,
  pdfOrientation: 'Vertical',
}

/**
 * PDF viewer settings for a course content block (RSCX-373): how completion is
 * measured and whether the student scrolls vertically or pages horizontally.
 * Expects to live inside a react-hook-form FormProvider; `name` is the path of
 * the content block (e.g. `contents.0`).
 */
export const PdfBlockOptions = ({ name, readOnly = false }) => {
  const { control, watch, setValue } = useFormContext()
  const completionMode = watch(`${name}.pdfCompletionMode`)

  return (
    <div className="flex flex-col gap-4 rounded border p-4">
      <span className="text-sm font-semibold">PDF viewer settings</span>
      <div className="flex flex-col gap-4 md:flex-row">
        <FormField
          control={control}
          name={`${name}.pdfCompletionMode`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>Completion requires</FormLabel>
              <FormControl>
                <Select
                  options={PDF_COMPLETION_MODES}
                  placeholder="Select completion requirement"
                  disabled={readOnly}
                  value={field.value ?? 'All Pages'}
                  onChange={(mode) => {
                    field.onChange(mode)
                    // The percent threshold only applies to percent mode; clear
                    // it on switch so the backend doesn't reject the payload.
                    if (mode !== 'Percent of Pages') {
                      setValue(`${name}.pdfCompletionPercent`, null, { shouldDirty: true })
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {completionMode === 'Percent of Pages' && (
          <FormField
            control={control}
            disabled={readOnly}
            name={`${name}.pdfCompletionPercent`}
            rules={{
              required: 'A percentage is required',
              min: { value: 1, message: 'Must be between 1 and 100' },
              max: { value: 100, message: 'Must be between 1 and 100' },
            }}
            render={({ field }) => (
              <FormItem className="w-40">
                <FormLabel>% of pages</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    placeholder="e.g. 80"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? null : Number(e.target.value))
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      <FormField
        control={control}
        name={`${name}.pdfOrientation`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Viewer orientation</FormLabel>
            <FormControl>
              <Select
                options={PDF_ORIENTATIONS}
                placeholder="Select orientation"
                disabled={readOnly}
                value={field.value ?? 'Vertical'}
                onChange={field.onChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}

PdfBlockOptions.propTypes = {
  name: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
}
